import { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase/config';
import { collection, getDocs, doc, updateDoc, deleteDoc, setDoc, query } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import Logo from '../components/Logo';
import { 
  PencilIcon, 
  TrashIcon, 
  PlusIcon,
  XMarkIcon,
  CircleStackIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { updatePaymentSettings, usePaymentSettings } from '../firebase/paymentSettings';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdminWithdrawalNotifications from '../components/AdminWithdrawalNotifications';
import IdCardManagement from '../components/IdCardManagement';
import IdCardGallery from '../components/IdCardGallery';
import { sendOTPEmail } from '../services/emailService';

function AdminDashboard() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const paymentSettings = usePaymentSettings();
  const [settings, setSettings] = useState({
    crypto: {
      bitcoin: {
        address: '',
        name: 'Bitcoin (BTC)'
      },
      ethereum: {
        address: '',
        name: 'Ethereum (ETH)'
      },
      usdt: {
        address: '',
        name: 'USDT (TRC20)'
      }
    },
    cashapp: {
      cashtag: '',
      instructions: [
        'Open your CashApp',
        'Click the Pay button',
        'Enter our cashtag',
        'Enter the amount you wish to deposit',
        'Add your account ID in the notes',
        'Confirm the payment'
      ]
    },
    paypal: {
      email: '',
      instructions: [
        'Log in to your PayPal account',
        'Click Send & Request',
        'Enter our PayPal email',
        'Enter the amount you wish to deposit',
        'Add your account ID in the notes',
        'Review and Send payment'
      ]
    },
    bank: {
      details: {
        bankName: '',
        accountName: '',
        accountNumber: '',
        routingNumber: '',
        swift: ''
      }
    }
  });
  const inputRefs = useRef({});
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showIdCardModal, setShowIdCardModal] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }

    fetchUsers();
    if (paymentSettings) {
      setSettings(paymentSettings);
    }
  }, [isAdmin, navigate, paymentSettings]);

  const fetchUsers = async () => {
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        fetchUsers();
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(error.message);
      }
    }
  };

  // Helper function to generate OTP codes
  const generateOTP = (prefix) => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  // Function to regenerate OTP codes for a user
  const regenerateUserOTP = async (user) => {
    try {
      // Generate new OTP codes
      const loginOTP = generateOTP('LOGIN');
      const withdrawalOTP = generateOTP('WDL');

      // Send OTP codes via email
      await sendOTPEmail(user.email, loginOTP, withdrawalOTP);

      // Update user document with new OTP codes
      await updateDoc(doc(db, 'users', user.id), {
        securityDetails: {
          loginOTP: loginOTP,
          withdrawalOTP: withdrawalOTP,
          otpCreatedAt: new Date().toISOString()
        }
      });

      alert(`New OTP codes have been sent to ${user.email}`);
      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error("Error regenerating OTP:", error);
      alert(`Error regenerating OTP: ${error.message}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updatePaymentSettings(settings);
    setSaving(false);
    setSaveStatus(result.success ? 'success' : 'error');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const CryptoAddressInput = ({ coin, coinData, isLast }) => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const nextCoin = Object.keys(settings.crypto).find((key, index, arr) => {
          return arr[index - 1] === coin;
        });
        if (nextCoin && inputRefs.current[nextCoin]) {
          inputRefs.current[nextCoin].focus();
        } else if (isLast) {
          const cashAppInput = document.querySelector('input[name="cashtag"]');
          if (cashAppInput) cashAppInput.focus();
        }
      }
    };

    const handleChange = (e) => {
      const value = e.target.value;
      setSettings(prev => ({
        ...prev,
        crypto: {
          ...prev.crypto,
          [coin]: {
            ...prev.crypto[coin],
            address: value
          }
        }
      }));
    };

    return (
      <div className="space-y-2 p-6 border border-slate-700 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 hover:shadow-xl transition-all duration-200 hover:border-slate-600">
        <label className="block text-sm font-medium text-blue-300">
          {coinData.name} Address
        </label>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <input
                ref={el => inputRefs.current[coin] = el}
                type="text"
                value={coinData.address}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                placeholder={`Enter ${coinData.name} address`}
                autoComplete="off"
              />
              {coinData.address && (
                <button
                  onClick={() => {
                    const input = inputRefs.current[coin];
                    input.select();
                    input.setSelectionRange(0, 99999);
                    navigator.clipboard.writeText(coinData.address);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-300 p-1 transition-colors duration-200"
                  title="Copy address"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </button>
              )}
            </div>
            {coinData.address && (
              <p className="mt-1 text-xs text-blue-200">
                Press Enter to move to next field
              </p>
            )}
          </div>
          {coinData.address && (
            <div className="w-32 h-32 bg-white rounded-xl p-2 border border-slate-600">
              <QRCodeSVG
                value={coinData.address}
                size={112}
                level="H"
                includeMargin={true}
                className="w-full h-full"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const UserDetailsModal = ({ user, onClose }) => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="card-glass bg-gradient-to-br from-slate-800/95 to-slate-900/95 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">User Details</h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* User ID Card */}
            {user.idCardUrl && (
              <div>
                <h4 className="text-sm font-medium text-blue-300 mb-2">ID Card</h4>
                <div 
                  className="relative aspect-[3/2] w-full rounded-xl overflow-hidden border border-slate-600 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setShowIdCardModal(true);
                  }}
                >
                  <img
                    src={user.idCardUrl}
                    alt="ID Card"
                    className="object-contain w-full h-full"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300?text=ID+Card+Not+Available';
                      e.target.alt = 'ID Card Not Available';
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                    <span className="text-white opacity-0 hover:opacity-100 transition-opacity">
                      Click to view full size
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-blue-300">Full Name</h4>
                <p className="mt-1 text-white">{user.fullName || user.name}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-300">Email</h4>
                <p className="mt-1 text-blue-200">{user.email}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-300">Phone</h4>
                <p className="mt-1 text-white">{user.mobileNumber || 'Not provided'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-300">Country</h4>
                <p className="mt-1 text-white">{user.country || 'Not provided'}</p>
              </div>
            </div>

            {/* Account Details */}
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-slate-700">
              <h4 className="text-sm font-medium text-blue-300 mb-3">Account Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-200">Balance</p>
                  <p className="text-lg font-semibold text-green-400">${user.balance?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Total Invested</p>
                  <p className="text-lg font-semibold text-blue-400">${user.totalInvested?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Total Profit</p>
                  <p className="text-lg font-semibold text-purple-400">${user.totalProfit?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Pending Withdrawals</p>
                  <p className="text-lg font-semibold text-orange-400">${user.pendingWithdrawals?.toLocaleString() || '0'}</p>
                </div>
              </div>
            </div>

            {/* Registration Info */}
            <div>
              <h4 className="text-sm font-medium text-blue-300 mb-2">Registration Info</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-200">Registration Date</p>
                  <p className="mt-1 text-white">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Not available'}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Account Status</p>
                  <p className="mt-1 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-600/20 to-emerald-600/20 text-green-400 border border-green-500/30">
                    Active
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-8">Admin Dashboard</h1>

          {/* Withdrawal Notifications Section */}
          <div className="mb-8">
            <AdminWithdrawalNotifications />
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <nav className="flex space-x-4" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                  activeTab === 'users'
                    ? 'bg-gradient-to-r from-blue-700/80 to-purple-700/80 text-white shadow-lg border border-blue-500/30'
                    : 'text-blue-300 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/10'
                }`}
              >
                Users
              </button>
              <button
                onClick={() => setActiveTab('idCards')}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                  activeTab === 'idCards'
                    ? 'bg-gradient-to-r from-blue-700/80 to-purple-700/80 text-white shadow-lg border border-blue-500/30'
                    : 'text-blue-300 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/10'
                }`}
              >
                ID Cards
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                  activeTab === 'settings'
                    ? 'bg-gradient-to-r from-blue-700/80 to-purple-700/80 text-white shadow-lg border border-blue-500/30'
                    : 'text-blue-300 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/10'
                }`}
              >
                Payment Settings
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="card-glass bg-gradient-to-br from-slate-800/90 to-slate-900/90 rounded-2xl shadow-xl border border-slate-700">
            {activeTab === 'users' && (
              <div className="p-6">
                {/* Existing users content */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">User Management</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg border border-blue-500/30"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create User</span>
          </button>
        </div>
        <div className="card-glass bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl shadow-xl border border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-gradient-to-r from-slate-800/80 to-slate-900/80">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">ID Card</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Balance</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Total Invested</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Total Profit</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">OTP Codes</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Active Investments</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-blue-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-transparent divide-y divide-slate-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-800/30 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-white">{user.fullName || user.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-blue-200">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                              {user.idCardUrl && (
                                <div>
                                  <h4 className="text-sm font-medium text-blue-300 mb-2">ID Card</h4>
                                  <div 
                                    className="relative aspect-[3/2] w-full rounded-xl overflow-hidden border border-slate-600 cursor-pointer hover:border-blue-500 transition-colors"
                                    onClick={() => {
                                      setSelectedUserId(user.id);
                                      setShowIdCardModal(true);
                                    }}
                                  >
                          <img
                            src={user.idCardUrl}
                            alt="ID Card"
                                      className="object-contain w-full h-full"
                                      onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/400x300?text=ID+Card+Not+Available';
                                        e.target.alt = 'ID Card Not Available';
                                      }}
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                                      <span className="text-white opacity-0 hover:opacity-100 transition-opacity">
                                        Click to view full size
                                      </span>
                        </div>
                                  </div>
                                </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-green-400 font-semibold">${user.balance?.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-blue-400 font-semibold">${user.totalInvested?.toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-purple-400 font-semibold">${user.totalProfit?.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="font-mono text-xs mb-1">
                          <span className="font-medium text-blue-300">Login:</span>{' '}
                          <span className="bg-slate-800 px-2 py-1 rounded-lg border border-slate-600 text-cyan-300">
                            {user.securityDetails?.loginOTP || 'Not set'}
                          </span>
                        </p>
                        <p className="font-mono text-xs mb-2">
                          <span className="font-medium text-blue-300">WDL:</span>{' '}
                          <span className="bg-slate-800 px-2 py-1 rounded-lg border border-slate-600 text-cyan-300">
                            {user.securityDetails?.withdrawalOTP || 'Not set'}
                          </span>
                        </p>
                        {(!user.securityDetails?.loginOTP || !user.securityDetails?.withdrawalOTP) && (
                          <button
                            onClick={() => regenerateUserOTP(user)}
                            className="text-xs bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 border border-blue-500/30"
                          >
                            Regenerate OTP
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 text-green-400 text-xs font-medium px-3 py-1 rounded-full border border-green-500/30">
                          {user.investments?.filter(inv => inv.status === 'active').length || 0} Active
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          setUserToEdit(user);
                          setShowEditModal(true);
                        }}
                          className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-400 hover:text-red-300 transition-colors duration-200"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
            )}
            
            {activeTab === 'idCards' && (
              <div className="p-6">
                <IdCardGallery />
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="p-6">
                <div className="max-w-4xl mx-auto">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Payment Settings</h2>
                    <p className="mt-1 text-sm text-blue-200">
                      Configure payment methods and addresses for deposits and withdrawals.
                    </p>
            </div>

                  {/* Crypto Addresses */}
                  <div className="space-y-6 mb-8">
                    <h3 className="text-lg font-medium text-white">Crypto Addresses</h3>
                    {Object.entries(settings.crypto).map(([coin, coinData], index, array) => (
                    <CryptoAddressInput 
                      key={coin} 
                      coin={coin} 
                        coinData={coinData}
                        isLast={index === array.length - 1}
                    />
                  ))}
                </div>

              {/* CashApp Settings */}
                  <div className="space-y-6 mb-8">
                    <h3 className="text-lg font-medium text-white">CashApp Settings</h3>
                    <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-blue-300">CashApp Cashtag</label>
                        <div className="mt-1">
                  <input
                    type="text"
                            name="cashtag"
                    value={settings.cashapp.cashtag}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              cashapp: { ...prev.cashapp, cashtag: e.target.value }
                            }))}
                            className="w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                            placeholder="Enter CashApp cashtag"
                        />
                      </div>
                    </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Instructions</label>
                        <div className="mt-1 space-y-2">
                        {settings.cashapp.instructions.map((instruction, index) => (
                            <div key={index} className="flex items-start">
                              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 text-sm font-medium border border-blue-500/30">
                              {index + 1}
                            </span>
                              <p className="ml-3 text-sm text-blue-200">{instruction}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                </div>
              </div>

                {/* PayPal Settings */}
                  <div className="space-y-6 mb-8">
                    <h3 className="text-lg font-medium text-white">PayPal Settings</h3>
                    <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-blue-300">PayPal Email</label>
                        <div className="mt-1">
                      <input
                        type="email"
                        value={settings.paypal.email}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              paypal: { ...prev.paypal, email: e.target.value }
                            }))}
                            className="w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                        placeholder="Enter PayPal email"
                      />
                    </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Instructions</label>
                        <div className="mt-1 space-y-2">
                        {settings.paypal.instructions.map((instruction, index) => (
                            <div key={index} className="flex items-start">
                              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 text-sm font-medium border border-blue-500/30">
                              {index + 1}
                            </span>
                              <p className="ml-3 text-sm text-blue-200">{instruction}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                  {/* Bank Details */}
                  <div className="space-y-6 mb-8">
                    <h3 className="text-lg font-medium text-white">Bank Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Bank Name</label>
                      <input
                        type="text"
                          value={settings.bank.details.bankName}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                          bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, bankName: e.target.value }
                            }
                          }))}
                          className="mt-1 w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Account Name</label>
                        <input
                          type="text"
                          value={settings.bank.details.accountName}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, accountName: e.target.value }
                            }
                          }))}
                          className="mt-1 w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                      />
                    </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Account Number</label>
                        <input
                          type="text"
                          value={settings.bank.details.accountNumber}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, accountNumber: e.target.value }
                            }
                          }))}
                          className="mt-1 w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                        />
                  </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">Routing Number</label>
                        <input
                          type="text"
                          value={settings.bank.details.routingNumber}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, routingNumber: e.target.value }
                            }
                          }))}
                          className="mt-1 w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                        />
                </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-300">SWIFT Code</label>
                        <input
                          type="text"
                          value={settings.bank.details.swift}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            bank: {
                              ...prev.bank,
                              details: { ...prev.bank.details, swift: e.target.value }
                            }
                          }))}
                          className="mt-1 w-full p-3 border border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-800/50 text-white placeholder-slate-400"
                        />
              </div>
                    </div>
                  </div>

              {/* Save Button */}
                  <div className="flex justify-end">
                    <button
                  onClick={handleSave}
                  disabled={saving}
                      className={`px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg border border-blue-500/30 ${
                        saving ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  {/* Save Status */}
                {saveStatus && (
                    <div className={`mt-4 p-4 rounded-xl ${
                      saveStatus === 'success' ? 'bg-green-900/20 text-green-400 border border-green-500/30' : 'bg-red-900/20 text-red-400 border border-red-500/30'
                    }`}>
                      {saveStatus === 'success' ? 'Settings saved successfully!' : 'Failed to save settings. Please try again.'}
                    </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
        />
      )}

      {showEditModal && (
        <EditUserModal 
          user={userToEdit}
          onClose={() => {
            setShowEditModal(false);
            setUserToEdit(null);
          }}
          onSuccess={(updatedUser) => {
            setShowEditModal(false);
            setUserToEdit(null);
            fetchUsers();
          }}
        />
      )}

      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      <IdCardModal
        isOpen={showIdCardModal}
        onClose={() => setShowIdCardModal(false)}
        idCardUrl={users.find(u => u.id === selectedUserId)?.idCardUrl}
      />
    </div>
  );
}

// Separate component definitions outside the main component
const CreateUserModal = ({ onClose, onSuccess }) => {
  const [localFormData, setLocalFormData] = useState({
    email: '',
    password: '',
    name: '',
    balance: 0,
    totalInvested: 0,
    totalProfit: 0,
    portfolio: {
      stocks: 0,
      crypto: 0,
      realEstate: 0,
      cash: 0
    },
    investments: []
  });

  // Helper function to generate OTP codes
  const generateOTP = (prefix) => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Generate OTP codes
      const loginOTP = generateOTP('LOGIN');
      const withdrawalOTP = generateOTP('WDL');

      // Create Firebase auth account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        localFormData.email,
        localFormData.password
      );

      // Send OTP codes via email
      await sendOTPEmail(localFormData.email, loginOTP, withdrawalOTP);

      // Create user document in Firestore with OTP codes
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: localFormData.email,
        name: localFormData.name,
        createdAt: new Date().toISOString(),
        balance: parseFloat(localFormData.balance) || 0,
        totalInvested: parseFloat(localFormData.totalInvested) || 0,
        totalProfit: parseFloat(localFormData.totalProfit) || 0,
        portfolio: {
          stocks: parseFloat(localFormData.portfolio.stocks) || 0,
          crypto: parseFloat(localFormData.portfolio.crypto) || 0,
          realEstate: parseFloat(localFormData.portfolio.realEstate) || 0,
          cash: parseFloat(localFormData.portfolio.cash) || 0
        },
        investments: [],
        status: 'active',
        role: 'user',
        accountDetails: {
          totalInvestment: parseFloat(localFormData.totalInvested) || 0,
          totalProfit: parseFloat(localFormData.totalProfit) || 0,
          availableBalance: parseFloat(localFormData.balance) || 0,
          pendingWithdrawals: 0,
          lastDeposit: 0,
          lastWithdrawal: 0,
          accountLevel: 'standard',
          lastLogin: new Date().toISOString(),
          accountType: 'standard',
          verificationStatus: 'verified'
        },
        securityDetails: {
          loginOTP: loginOTP,
          withdrawalOTP: withdrawalOTP,
          otpCreatedAt: new Date().toISOString()
        }
      });

      alert(`User created successfully! OTP codes have been sent to ${localFormData.email}`);
      onSuccess();
    } catch (error) {
      console.error("Error creating user:", error);
      alert(error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="card-glass bg-gradient-to-br from-slate-800/95 to-slate-900/95 rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Create New User</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors duration-200">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-blue-300">Email</label>
            <input
              type="email"
              required
              className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
              value={localFormData.email}
              onChange={(e) => setLocalFormData(prev => ({...prev, email: e.target.value}))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-blue-300">Password</label>
            <input
              type="password"
              required
              className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
              value={localFormData.password}
              onChange={(e) => setLocalFormData(prev => ({...prev, password: e.target.value}))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-blue-300">Name</label>
            <input
              type="text"
              required
              className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
              value={localFormData.name}
              onChange={(e) => setLocalFormData(prev => ({...prev, name: e.target.value}))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-blue-300">Balance</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                value={localFormData.balance}
                onChange={(e) => setLocalFormData(prev => ({...prev, balance: e.target.value}))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-blue-300">Total Invested</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                value={localFormData.totalInvested}
                onChange={(e) => setLocalFormData(prev => ({...prev, totalInvested: e.target.value}))}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-blue-300 mb-2">Portfolio Distribution</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-blue-200">Stocks (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.portfolio.stocks}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, stocks: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-blue-200">Crypto (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.portfolio.crypto}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, crypto: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-blue-200">Real Estate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.portfolio.realEstate}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, realEstate: e.target.value}
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm text-blue-200">Cash (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.portfolio.cash}
                  onChange={(e) => setLocalFormData(prev => ({
                    ...prev,
                    portfolio: {...prev.portfolio, cash: e.target.value}
                  }))}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-blue-300 bg-transparent border border-slate-600 rounded-xl hover:bg-slate-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              Cancel
            </button>
          <button
            type="submit"
              className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg border border-blue-500/30"
          >
            Create User
          </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditUserModal = ({ user, onClose, onSuccess }) => {
  const [localFormData, setLocalFormData] = useState({
    balance: user?.balance || 0,
    totalInvested: user?.totalInvested || 0,
    totalProfit: user?.totalProfit || 0,
    portfolio: {
      stocks: user?.portfolio?.stocks || 0,
      crypto: user?.portfolio?.crypto || 0,
      realEstate: user?.portfolio?.realEstate || 0,
      cash: user?.portfolio?.cash || 0
    },
    investments: user?.investments || []
  });

  const [newInvestment, setNewInvestment] = useState({
    planName: '',
    amount: '',
    startDate: '',
    endDate: '',
    status: 'active'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [activeTab, setActiveTab] = useState('account'); // 'account' or 'investments'
  const [editingInvestment, setEditingInvestment] = useState(null);

  const handleAddInvestment = () => {
    if (!newInvestment.planName || !newInvestment.amount || !newInvestment.startDate) {
      setFeedback({
        type: 'error',
        message: 'Please fill in all investment fields'
      });
      return;
    }

    // Calculate end date based on 21-day period
    const startDate = new Date(newInvestment.startDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 21);

    // Calculate expected return based on plan
    let expectedReturn = 0;
    const amount = parseFloat(newInvestment.amount);
    switch(newInvestment.planName) {
      case 'Standard Plan':
        if (amount >= 1000 && amount <= 5000) {
          expectedReturn = amount * 10;
        }
        break;
      case 'Premium Plan':
        if (amount >= 6000 && amount <= 10000) {
          expectedReturn = amount * 15;
        }
        break;
      case 'Advanced Plan':
        if (amount >= 10000 && amount <= 14000) {
          expectedReturn = amount * 20;
        }
        break;
      case 'Platinum Plan':
        if (amount >= 25000 && amount <= 45000) {
          expectedReturn = amount * 25;
        }
        break;
      case 'Diamond Plan':
        if (amount >= 50000) {
          expectedReturn = amount * 30;
        }
        break;
      default:
        expectedReturn = 0;
    }

    setLocalFormData(prev => ({
      ...prev,
      investments: [...prev.investments, { 
        ...newInvestment, 
        id: Date.now().toString(),
        endDate: endDate.toISOString().split('T')[0],
        expectedReturn: expectedReturn,
        profit: 0,
        balance: 0
      }]
    }));

    setNewInvestment({
      planName: '',
      amount: '',
      startDate: '',
      status: 'active'
    });

    setFeedback({
      type: 'success',
      message: 'Investment added successfully'
    });

    // Clear feedback after 3 seconds
    setTimeout(() => setFeedback({ type: '', message: '' }), 3000);
  };

  const handleRemoveInvestment = (id) => {
    setLocalFormData(prev => ({
      ...prev,
      investments: prev.investments.filter(inv => inv.id !== id)
    }));

    setFeedback({
      type: 'success',
      message: 'Investment removed successfully'
    });

    // Clear feedback after 3 seconds
    setTimeout(() => setFeedback({ type: '', message: '' }), 3000);
  };

  const handleEditInvestment = (investment) => {
    setEditingInvestment({ ...investment });
  };

  const handleSaveInvestmentEdit = () => {
    if (!editingInvestment) return;

    setLocalFormData(prev => ({
      ...prev,
      investments: prev.investments.map(inv => 
        inv.id === editingInvestment.id ? editingInvestment : inv
      )
    }));

    setEditingInvestment(null);
    setFeedback({
      type: 'success',
      message: 'Investment updated successfully'
    });

    // Clear feedback after 3 seconds
    setTimeout(() => setFeedback({ type: '', message: '' }), 3000);
  };

  const handleCancelInvestmentEdit = () => {
    setEditingInvestment(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      const updates = {
        balance: parseFloat(localFormData.balance),
        totalInvested: parseFloat(localFormData.totalInvested),
        totalProfit: parseFloat(localFormData.totalProfit),
        portfolio: {
          stocks: parseFloat(localFormData.portfolio.stocks),
          crypto: parseFloat(localFormData.portfolio.crypto),
          realEstate: parseFloat(localFormData.portfolio.realEstate),
          cash: parseFloat(localFormData.portfolio.cash)
        },
        investments: localFormData.investments
      };

      await updateDoc(doc(db, 'users', user.id), updates);
      setFeedback({
        type: 'success',
        message: 'User updated successfully'
      });
      onSuccess(localFormData);
    } catch (error) {
      console.error("Error updating user:", error);
      setFeedback({
        type: 'error',
        message: error.message || 'Failed to update user'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="card-glass bg-gradient-to-br from-slate-800/95 to-slate-900/95 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-slate-700"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Edit User</h2>
              <p className="text-sm text-blue-200 mt-1">{user.email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Feedback Message */}
          {feedback.message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-4 p-4 rounded-xl ${
                feedback.type === 'success' 
                  ? 'bg-green-900/20 text-green-400 border border-green-500/30'
                  : 'bg-red-900/20 text-red-400 border border-red-500/30'
              }`}
            >
              {feedback.message}
            </motion.div>
          )}

          {/* Tabs */}
          <div className="border-b border-slate-700 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('account')}
                className={`${
                  activeTab === 'account'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
              >
                Account Details
              </button>
              <button
                onClick={() => setActiveTab('investments')}
                className={`${
                  activeTab === 'investments'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
              >
                Investments
              </button>
            </nav>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {activeTab === 'account' ? (
          <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-blue-300">Balance</label>
                    <div className="mt-1 relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-400 sm:text-sm">$</span>
                      </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                        className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.balance}
                  onChange={(e) => setLocalFormData(prev => ({...prev, balance: e.target.value}))}
                />
                    </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-300">Total Invested</label>
                    <div className="mt-1 relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-400 sm:text-sm">$</span>
                      </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                        className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                  value={localFormData.totalInvested}
                  onChange={(e) => setLocalFormData(prev => ({...prev, totalInvested: e.target.value}))}
                />
                    </div>
              </div>
            </div>
              <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-blue-300">Total Profit</label>
                    <div className="mt-1 relative rounded-xl shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-400 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                        value={localFormData.totalProfit}
                        onChange={(e) => setLocalFormData(prev => ({...prev, totalProfit: e.target.value}))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
              {/* Current Investments List */}
                <div className="card-glass bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-slate-700">
                  <h3 className="text-lg font-medium text-white mb-4">Manage Investments</h3>
                  <div className="space-y-4">
                {localFormData.investments.map((investment) => (
                      <motion.div
                        key={investment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card-glass bg-gradient-to-br from-slate-800/30 to-slate-900/30 p-4 rounded-xl shadow-sm border border-slate-600"
                      >
                        {editingInvestment?.id === investment.id ? (
                          // Edit Mode
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="text-lg font-semibold text-white">{investment.planName}</h4>
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={handleSaveInvestmentEdit}
                                  className="px-3 py-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 border border-green-500/30"
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelInvestmentEdit}
                                  className="px-3 py-1 bg-gradient-to-r from-slate-600 to-gray-600 text-white text-sm rounded-lg hover:from-slate-700 hover:to-gray-700 transition-all duration-200 border border-slate-500/30"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-blue-300">Amount</label>
                                <div className="mt-1 relative rounded-xl shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-slate-400 sm:text-sm">$</span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                                    value={editingInvestment.amount}
                                    onChange={(e) => setEditingInvestment(prev => ({...prev, amount: e.target.value}))}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-blue-300">Profit</label>
                                <div className="mt-1 relative rounded-xl shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-slate-400 sm:text-sm">$</span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                                    value={editingInvestment.profit || 0}
                                    onChange={(e) => setEditingInvestment(prev => ({...prev, profit: e.target.value}))}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-blue-300">Balance</label>
                                <div className="mt-1 relative rounded-xl shadow-sm">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-slate-400 sm:text-sm">$</span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                                    value={editingInvestment.balance || 0}
                                    onChange={(e) => setEditingInvestment(prev => ({...prev, balance: e.target.value}))}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-blue-300">Status</label>
                                <select
                                  value={editingInvestment.status}
                                  onChange={(e) => setEditingInvestment(prev => ({...prev, status: e.target.value}))}
                                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white"
                                >
                                  <option value="active">Active</option>
                                  <option value="completed">Completed</option>
                                  <option value="paused">Paused</option>
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-blue-300">Start Date</label>
                                <input
                                  type="date"
                                  value={editingInvestment.startDate}
                                  onChange={(e) => setEditingInvestment(prev => ({...prev, startDate: e.target.value}))}
                                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-blue-300">End Date</label>
                                <input
                                  type="date"
                                  value={editingInvestment.endDate}
                                  onChange={(e) => setEditingInvestment(prev => ({...prev, endDate: e.target.value}))}
                                  className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="text-lg font-semibold text-white">{investment.planName}</h4>
                                <p className="text-sm text-blue-200">
                                  {new Date(investment.startDate).toLocaleDateString()} - {new Date(investment.endDate).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  investment.status === 'active' ? 'bg-gradient-to-r from-green-600/20 to-emerald-600/20 text-green-400 border border-green-500/30' : 
                                  investment.status === 'completed' ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 text-blue-400 border border-blue-500/30' : 'bg-gradient-to-r from-slate-600/20 to-gray-600/20 text-slate-400 border border-slate-500/30'
                                }`}>
                                  {investment.status}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleEditInvestment(investment)}
                                  className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveInvestment(investment.id)}
                                  className="text-red-400 hover:text-red-300 transition-colors duration-200"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-blue-200">Amount</p>
                                <p className="font-semibold text-white">${parseFloat(investment.amount).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-blue-200">Profit</p>
                                <p className="font-semibold text-green-400">${parseFloat(investment.profit || investment.expectedReturn || 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-blue-200">Balance</p>
                                <p className="font-semibold text-white">${parseFloat(investment.balance || 0).toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-blue-200">Expected Return</p>
                                <p className="font-semibold text-purple-400">${parseFloat(investment.expectedReturn || 0).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </motion.div>
                ))}
                  </div>
              </div>

              {/* Add New Investment Form */}
                <div className="card-glass bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl border border-slate-700 p-4">
                  <h3 className="text-lg font-medium text-white mb-4">Add New Investment</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-sm font-medium text-blue-300">Plan Name</label>
                    <select
                      value={newInvestment.planName}
                      onChange={(e) => setNewInvestment(prev => ({ ...prev, planName: e.target.value }))}
                      className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white"
                    >
                      <option value="">Select Plan</option>
                      <option value="Standard Plan">Standard Plan ($1,000 - $5,000)</option>
                      <option value="Premium Plan">Premium Plan ($6,000 - $10,000)</option>
                      <option value="Advanced Plan">Advanced Plan ($10,000 - $14,000)</option>
                      <option value="Platinum Plan">Platinum Plan ($25,000 - $45,000)</option>
                      <option value="Diamond Plan">Diamond Plan ($50,000+)</option>
                    </select>
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-blue-300">Amount</label>
                      <div className="mt-1 relative rounded-xl shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-slate-400 sm:text-sm">$</span>
                        </div>
                    <input
                      type="number"
                      min="1000"
                      max="1000000"
                      value={newInvestment.amount}
                      onChange={(e) => setNewInvestment(prev => ({ ...prev, amount: e.target.value }))}
                          className="pl-7 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white placeholder-slate-400"
                      placeholder="Enter amount"
                    />
                      </div>
                  </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-blue-300">Start Date</label>
                  <input
                    type="date"
                    value={newInvestment.startDate}
                    onChange={(e) => setNewInvestment(prev => ({ ...prev, startDate: e.target.value }))}
                    className="mt-1 block w-full rounded-xl border border-slate-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-slate-800/50 text-white"
                  />
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={handleAddInvestment}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg border border-blue-500/30"
                  >
                    Add Investment
                  </button>
                </div>
              </div>
            </div>
            )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-blue-300 bg-transparent border border-slate-600 rounded-xl hover:bg-slate-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              Cancel
            </button>
            <button
                type="submit"
                disabled={isLoading}
                className={`px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  'Save Changes'
                )}
            </button>
          </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

const IdCardModal = ({ isOpen, onClose, idCardUrl }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="card-glass bg-gradient-to-br from-slate-800/95 to-slate-900/95 rounded-2xl p-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">ID Card Verification</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors duration-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="relative aspect-[3/2] w-full rounded-xl overflow-hidden border border-slate-600">
          <img
            src={idCardUrl}
            alt="ID Card"
            className="object-contain w-full h-full"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=ID+Card+Not+Available';
              e.target.alt = 'ID Card Not Available';
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 